import { gql } from "@apollo/client";

// Shapes verified against appointify-service `origin/master` — the REF, not a
// working tree. The checkout on a dev machine sits on a feature branch, and
// reading it instead of the ref once produced an estimate claiming this
// resolver did not exist. It shipped 2026-08-25.

/**
 * Turn a booking link's slug into a professional.
 *
 * Public: App Check only, no authentication — a share link is opened by
 * strangers with no account.
 *
 * It resolves the SLUG ALONE and its answer never varies by query parameter,
 * because attribution must not affect authorization or booking eligibility.
 * Anything in the URL is ours to carry.
 */
export const RESOLVE_BOOKING_SLUG = ({"kind":"Document","definitions":[{"kind":"OperationDefinition","operation":"query","name":{"kind":"Name","value":"ResolveBookingSlug"},"variableDefinitions":[{"kind":"VariableDefinition","variable":{"kind":"Variable","name":{"kind":"Name","value":"slug"}},"type":{"kind":"NonNullType","type":{"kind":"NamedType","name":{"kind":"Name","value":"String"}}},"directives":[]}],"directives":[],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"resolveBookingSlug"},"arguments":[{"kind":"Argument","name":{"kind":"Name","value":"slug"},"value":{"kind":"Variable","name":{"kind":"Name","value":"slug"}}}],"directives":[],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"outcome"},"arguments":[],"directives":[]},{"kind":"Field","name":{"kind":"Name","value":"professionalId"},"arguments":[],"directives":[]},{"kind":"Field","name":{"kind":"Name","value":"canonicalSlug"},"arguments":[],"directives":[]}]}}]}}]})







;

/*
 * THE POLICY IN THE CLIENT'S LANGUAGE (rc.7). `cancellationPolicyDisplay` has
 * taken a locale since booking-v1 1.6.0; Book always read the default (EN).
 * Spanish exists for every policy Prod books (IN_PERSON_ONLY). It is
 * Product-approved, content-hashed text that professionals assent to, so it
 * stays the SERVER's words: no local copy. Where Spanish has no approved
 * wording the server answers null, so a Spanish read also asks for the English
 * text, in the same request (`withEnglish`), and shows that rather than
 * nothing. `locale` on the answer is the language actually served.
 */
export const GET_PROFESSIONAL_PROFILE = ({"kind":"Document","definitions":[{"kind":"OperationDefinition","operation":"query","name":{"kind":"Name","value":"GetProfessionalProfile"},"variableDefinitions":[{"kind":"VariableDefinition","variable":{"kind":"Variable","name":{"kind":"Name","value":"professionalId"}},"type":{"kind":"NonNullType","type":{"kind":"NamedType","name":{"kind":"Name","value":"ID"}}},"directives":[]},{"kind":"VariableDefinition","variable":{"kind":"Variable","name":{"kind":"Name","value":"locale"}},"type":{"kind":"NonNullType","type":{"kind":"NamedType","name":{"kind":"Name","value":"BookingCopyLocale"}}},"defaultValue":{"kind":"EnumValue","value":"EN"},"directives":[]},{"kind":"VariableDefinition","variable":{"kind":"Variable","name":{"kind":"Name","value":"withEnglish"}},"type":{"kind":"NonNullType","type":{"kind":"NamedType","name":{"kind":"Name","value":"Boolean"}}},"defaultValue":{"kind":"BooleanValue","value":false},"directives":[]}],"directives":[],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"getProfessionalProfile"},"arguments":[{"kind":"Argument","name":{"kind":"Name","value":"professionalId"},"value":{"kind":"Variable","name":{"kind":"Name","value":"professionalId"}}}],"directives":[],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"professionalId"},"arguments":[],"directives":[]},{"kind":"Field","name":{"kind":"Name","value":"businessName"},"arguments":[],"directives":[]},{"kind":"Field","name":{"kind":"Name","value":"businessDescription"},"arguments":[],"directives":[]},{"kind":"Field","name":{"kind":"Name","value":"profilePhotoURL"},"arguments":[],"directives":[]},{"kind":"Field","name":{"kind":"Name","value":"addressDisplay"},"arguments":[],"directives":[]},{"kind":"Field","name":{"kind":"Name","value":"timezone"},"arguments":[],"directives":[]},{"kind":"Field","name":{"kind":"Name","value":"totalCompletedAppointments"},"arguments":[],"directives":[]},{"kind":"Field","name":{"kind":"Name","value":"acceptsInAppPayments"},"arguments":[],"directives":[]},{"kind":"Field","name":{"kind":"Name","value":"isAcceptingBookings"},"arguments":[],"directives":[]},{"kind":"Field","name":{"kind":"Name","value":"collectionMode"},"arguments":[],"directives":[]},{"kind":"Field","name":{"kind":"Name","value":"bookingConfirmationMode"},"arguments":[],"directives":[]},{"kind":"Field","name":{"kind":"Name","value":"requiresClientAccount"},"arguments":[],"directives":[]},{"kind":"Field","name":{"kind":"Name","value":"cancellationPolicyDisplay"},"arguments":[{"kind":"Argument","name":{"kind":"Name","value":"locale"},"value":{"kind":"Variable","name":{"kind":"Name","value":"locale"}}}],"directives":[],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"policyType"},"arguments":[],"directives":[]},{"kind":"Field","name":{"kind":"Name","value":"locale"},"arguments":[],"directives":[]},{"kind":"Field","name":{"kind":"Name","value":"headline"},"arguments":[],"directives":[]},{"kind":"Field","name":{"kind":"Name","value":"body"},"arguments":[],"directives":[]}]}},{"kind":"Field","alias":{"kind":"Name","value":"cancellationPolicyDisplayEnglish"},"name":{"kind":"Name","value":"cancellationPolicyDisplay"},"arguments":[{"kind":"Argument","name":{"kind":"Name","value":"locale"},"value":{"kind":"EnumValue","value":"EN"}}],"directives":[{"kind":"Directive","name":{"kind":"Name","value":"include"},"arguments":[{"kind":"Argument","name":{"kind":"Name","value":"if"},"value":{"kind":"Variable","name":{"kind":"Name","value":"withEnglish"}}}]}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"policyType"},"arguments":[],"directives":[]},{"kind":"Field","name":{"kind":"Name","value":"locale"},"arguments":[],"directives":[]},{"kind":"Field","name":{"kind":"Name","value":"headline"},"arguments":[],"directives":[]},{"kind":"Field","name":{"kind":"Name","value":"body"},"arguments":[],"directives":[]}]}},{"kind":"Field","name":{"kind":"Name","value":"services"},"arguments":[],"directives":[],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"id"},"arguments":[],"directives":[]},{"kind":"Field","name":{"kind":"Name","value":"name"},"arguments":[],"directives":[]},{"kind":"Field","name":{"kind":"Name","value":"description"},"arguments":[],"directives":[]},{"kind":"Field","name":{"kind":"Name","value":"durationMinutes"},"arguments":[],"directives":[]},{"kind":"Field","name":{"kind":"Name","value":"priceInCents"},"arguments":[],"directives":[]},{"kind":"Field","name":{"kind":"Name","value":"appCollectableInCents"},"arguments":[],"directives":[]}]}}]}}]}}]})













































;

export type ResolveOutcome = "RESOLVED" | "MOVED" | "NOT_BOOKABLE" | "UNAVAILABLE";

export interface ResolveBookingSlugData {
  resolveBookingSlug: {
    outcome: ResolveOutcome;
    /** Present for RESOLVED, MOVED and NOT_BOOKABLE. Null for UNAVAILABLE. */
    professionalId: string | null;
    /** The CURRENT slug. Differs from the request only on MOVED. */
    canonicalSlug: string | null;
  };
}

/**
 * The professional's cancellation policy, IN THE SERVER'S OWN WORDS.
 *
 * Added by `booking-v1@1.2.0`. It is the first thing in the contract that
 * carries cancellation copy BEFORE a booking exists, which is what the Review
 * screen needed and never had — until now that screen showed a placeholder
 * saying the terms would appear later, because the only policy fact in the
 * schema (`cancellationPolicyType`) arrived on the create RESULT, after the
 * client had already committed.
 *
 * The contract's frozen decision is that this is the ONLY approved source for
 * cancellation copy on the pre-create Review screen. So there is no fallback
 * constant here and there must not be one: a default that says 24 hours becomes
 * a lie the moment one professional is set to 48, on the screen a client reads
 * to decide whether to commit and later quotes back when they cancel.
 *
 * `policyType` is typed as an open string rather than the schema's enum. The
 * enum has exactly one member today (`V1_24H`); a value added server-side must
 * not become a permissive fall-through at this boundary.
 */
export interface BookingCancellationPolicyDisplay {
  policyType: string;
  /** The locale actually SERVED ("EN" | "ES"), which is not necessarily the one asked for. */
  locale: string;
  headline: string;
  body: string;
}

export interface PublicService {
  id: string;
  name: string;
  description: string | null;
  durationMinutes: number;
  priceInCents: number;
  /** booking-v1 1.14.0. What the app collects at booking for this service; null when it cannot be stated. */
  appCollectableInCents?: number | null;
}

export interface PublicProfessionalProfile {
  professionalId: string;
  businessName: string;
  businessDescription: string | null;
  profilePhotoURL: string | null;
  addressDisplay: string | null;
  timezone: string;
  // `averageRating` and `reviewCount` were REMOVED from the public schema on
  // 2026-08-26 — there was never a review write path, so the rating was always
  // null. A removed field is now an HTTP 400 with no `data`, so continuing to
  // select it would have broken every profile on the surface.
  totalCompletedAppointments: number;
  acceptsInAppPayments: boolean;
  /** booking-v1 1.14.0. IN_PERSON_ONLY, DEPOSIT_REQUIRED or FULL_PREPAY_REQUIRED (an open string here); null when unreadable. */
  collectionMode?: string | null;
  /** booking-v1 1.19.0 (gate open only). INSTANT or REQUEST, an OPEN string: anything else reads as unknown. */
  bookingConfirmationMode?: string | null;
  isAcceptingBookings: boolean;
  /**
   * Whether this professional requires a client account.
   *
   * Product: account-required gating happens BEFORE slot selection, not at
   * commit — so a client is never walked through picking a time only to be
   * stopped at the end.
   */
  requiresClientAccount: boolean;
  /**
   * NULL IS A STATEMENT, NOT AN ABSENCE.
   *
   * The contract documents null as "the policy cannot be represented safely" —
   * the persisted policy is missing or unrecognised — and instructs consumers
   * to fail closed rather than substitute local copy. So null must never be
   * read as "this professional has no cancellation policy", and it must never
   * be papered over with our own words. What it licenses is showing NOTHING
   * that describes terms.
   */
  cancellationPolicyDisplay: BookingCancellationPolicyDisplay | null;
  /** The English answer, read only alongside a Spanish one: what is shown when Spanish has no approved wording. */
  cancellationPolicyDisplayEnglish?: BookingCancellationPolicyDisplay | null;
  services: PublicService[];
}

export interface GetProfessionalProfileData {
  getProfessionalProfile: PublicProfessionalProfile;
}
